import React, { useState } from 'react';
import { useToast } from '../common/Toast';
import { subscriptionService } from '../../services/subscription.service';
import { theme } from '../../config/theme';

/**
 * AutoRenewToggle
 * A premium styled toggle that calls the toggleAutoRenew API.
 *
 * @param {boolean} enabled   - Current auto-renew state
 * @param {boolean} disabled  - Disable interaction (e.g. non-Razorpay subs)
 * @param {Function} onChange - Called with the new boolean value after API success
 */
const AutoRenewToggle = ({ enabled, disabled = false, onChange }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleToggle = () => {
    if (disabled || loading) return;
    if (enabled) {
      // Turning off — ask for confirmation first
      setShowConfirm(true);
    } else {
      doToggle(true);
    }
  };

  const doToggle = async (newValue) => {
    setShowConfirm(false);
    setLoading(true);
    try {
      await subscriptionService.toggleAutoRenew(newValue);
      onChange?.(newValue);
      toast.success(newValue ? 'Auto-renew enabled' : 'Auto-renew disabled');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update auto-renew setting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.sm,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          userSelect: 'none',
        }}
        onClick={handleToggle}
        id="auto-renew-toggle"
        role="switch"
        aria-checked={enabled}
        aria-label="Toggle auto-renew"
      >
        {/* Track */}
        <div
          style={{
            width: '44px',
            height: '24px',
            borderRadius: '12px',
            backgroundColor: enabled ? theme.colors.success : theme.colors.gray[300],
            position: 'relative',
            transition: 'background-color 0.25s ease',
            flexShrink: 0,
          }}
        >
          {/* Knob */}
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              position: 'absolute',
              top: '2px',
              left: enabled ? '22px' : '2px',
              transition: 'left 0.25s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </div>
        <span style={{
          fontSize: theme.typography.fontSize.sm,
          color: enabled ? theme.colors.success : theme.colors.text.secondary,
          fontWeight: theme.typography.fontWeight.medium,
        }}>
          {loading ? 'Updating…' : enabled ? 'Auto-renews' : 'Auto-renew off'}
        </span>
      </div>

      {/* Confirm modal when turning OFF */}
      {showConfirm && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
          }}
          onClick={() => setShowConfirm(false)}
        >
          <div
            style={{
              backgroundColor: theme.colors.white,
              borderRadius: theme.borderRadius.xl,
              padding: theme.spacing['2xl'],
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.sm }}>
              Disable Auto-Renew?
            </h3>
            <p style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm, marginBottom: theme.spacing.xl }}>
              Your subscription will remain active until the current period ends. After that, it will expire and you'll be moved to the Free plan.
            </p>
            <div style={{ display: 'flex', gap: theme.spacing.md, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                  borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.border.light}`,
                  background: 'none', cursor: 'pointer', fontWeight: theme.typography.fontWeight.medium
                }}
              >
                Keep Active
              </button>
              <button
                onClick={() => doToggle(false)}
                style={{
                  padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                  borderRadius: theme.borderRadius.md, border: 'none',
                  backgroundColor: theme.colors.error, color: '#fff',
                  cursor: 'pointer', fontWeight: theme.typography.fontWeight.medium
                }}
              >
                Disable
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AutoRenewToggle;
